import React, { useState, useEffect } from "react";
import { ConversationHistory } from "./ConversationHistory";
import { ChatArea } from "./ChatArea";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { getObras } from "@/services/obraStorageService";

export interface Message {
  id: string;
  type: "user" | "ai";
  content: string;
  timestamp: Date;
  actions?: Array<{
    type: string;
    description: string;
    count?: number;
  }>;
  source?: string;
  finishReason?: string;
  model?: string;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
}

export interface Conversation {
  id: string;
  title: string;
  lastMessage: string;
  timestamp: Date;
  type: "consulta" | "acao" | "norma" | "custo";
  messages: Message[];
}

/** Obra exibida no seletor de contexto do cabeçalho. */
export interface ObraOption {
  id: string;
  nome: string;
}

/** Classifica a conversa pelo teor da pergunta — alimenta o ícone do histórico. */
const inferirTipo = (texto: string): Conversation["type"] => {
  const t = texto.toLowerCase();
  if (/\b(tarefa|criar|agendar|lembrete|adicionar)\b/.test(t)) return "acao";
  if (/\b(norma|nbr|abnt|especifica|regulament)/.test(t)) return "norma";
  if (/\b(custo|preço|preco|valor|orçament|orcament|r\$)/.test(t)) return "custo";
  return "consulta";
};

/** Título curto a partir da primeira pergunta, sem cortar no meio da palavra. */
const gerarTitulo = (texto: string): string => {
  const limpo = texto.trim().replace(/\s+/g, " ");
  if (limpo.length <= 48) return limpo;
  const corte = limpo.slice(0, 48);
  const ultimoEspaco = corte.lastIndexOf(" ");
  return `${corte.slice(0, ultimoEspaco > 20 ? ultimoEspaco : 48)}…`;
};

const AIAssistant = () => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [obras, setObras] = useState<ObraOption[]>([]);
  const [obraId, setObraId] = useState<string | null>(null);
  const { toast } = useToast();

  const createNewConversation = () => {
    const newConversation: Conversation = {
      id: Date.now().toString(),
      title: "Nova Conversa",
      lastMessage: "",
      timestamp: new Date(),
      type: "consulta",
      messages: []
    };

    setCurrentConversation(newConversation);
    setHistoryOpen(false);
    return newConversation;
  };

  // Inicializa o chat e carrega as obras para o seletor de contexto
  useEffect(() => {
    createNewConversation();

    const lista = getObras().map((o) => ({ id: o.id, nome: o.nomeObra }));
    setObras(lista);
    setObraId((atual) => atual ?? lista[0]?.id ?? null);
  }, []);

  const selectConversation = (conversation: Conversation) => {
    setCurrentConversation(conversation);
  };

  const sendMessage = async (content: string, options?: { detailLevel?: "concise" | "detailed" }) => {
    let activeConversation = currentConversation;

    if (!activeConversation) {
      activeConversation = createNewConversation();
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: "user",
      content,
      timestamp: new Date()
    };

    const primeiraPergunta = activeConversation.messages.length === 0;

    const updatedConversation: Conversation = {
      ...activeConversation,
      messages: [...activeConversation.messages, userMessage],
      lastMessage: content,
      timestamp: new Date(),
      title: primeiraPergunta ? gerarTitulo(content) : activeConversation.title,
      type: primeiraPergunta ? inferirTipo(content) : activeConversation.type
    };

    setCurrentConversation(updatedConversation);
    setIsLoading(true);

    // Ajusta o nível de detalhamento no último turno do usuário
    const detailLevel = (options?.detailLevel ?? "detailed") as "concise" | "detailed";
    let contentToSend = content;
    if (detailLevel === "concise") {
      contentToSend = `Responda de forma concisa (até 3 parágrafos): ${content}`;
    } else {
      contentToSend = `Responda com alto nível de detalhe, com exemplos, etapas e referências quando possível: ${content}`;
    }

    // Contexto da obra selecionada no cabeçalho
    const obraSelecionada = obras.find((o) => o.id === obraId);
    if (obraSelecionada) {
      contentToSend = `[Obra em contexto: ${obraSelecionada.nome}] ${contentToSend}`;
    }

    // Monta histórico para a função Edge (user/assistant)
    const history = updatedConversation.messages.map((m, idx, arr) => ({
      role: m.type === "user" ? "user" : "assistant",
      content: idx === arr.length - 1 && m.type === "user" ? contentToSend : m.content,
    }));

    const { data, error } = await supabase.functions.invoke("ai-chat", {
      body: { messages: history },
    });

    if (error) {
      console.error("Erro ao chamar ai-chat:", error);
      const aiMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: "ai",
        content: "Desculpe, ocorreu um erro ao consultar a IA. Tente novamente em instantes.",
        timestamp: new Date(),
      };

      const finalConversation = {
        ...updatedConversation,
        messages: [...updatedConversation.messages, aiMessage]
      };

      setCurrentConversation(finalConversation);
      setConversations(prev => {
        const existing = prev.find(c => c.id === finalConversation.id);
        if (existing) {
          return prev.map(c => c.id === finalConversation.id ? finalConversation : c);
        }
        return [finalConversation, ...prev];
      });
      setIsLoading(false);

      toast({
        title: "Erro ao consultar a IA",
        description: error.message ?? "Falha ao obter resposta",
        variant: "destructive",
      });
      return;
    }

    const generatedText: string = data?.generatedText ?? "Desculpe, não recebi uma resposta.";
    const finishReason: string | null = data?.finish_reason ?? null;
    const usage = data?.usage ?? null;
    const modelUsed: string | undefined = data?.model;

    const aiMessage: Message = {
      id: (Date.now() + 1).toString(),
      type: "ai",
      content: generatedText,
      timestamp: new Date(),
      finishReason: finishReason ?? undefined,
      model: modelUsed,
      usage: usage ?? undefined,
      // Mantém lógica simples de ações/fonte com base no input do usuário, como no mock anterior
      actions: content.toLowerCase().includes("tarefa") ? [
        { type: "task_created", description: "1 tarefa criada", count: 1 }
      ] : undefined,
      source: content.toLowerCase().includes("norma") ? "ABNT NBR 15575" : undefined
    };

    const finalConversation = {
      ...updatedConversation,
      messages: [...updatedConversation.messages, aiMessage]
    };

    setCurrentConversation(finalConversation);

    // Atualizar lista de conversas
    setConversations(prev => {
      const existing = prev.find(c => c.id === finalConversation.id);
      if (existing) {
        return prev.map(c => c.id === finalConversation.id ? finalConversation : c);
      }
      return [finalConversation, ...prev];
    });

    setIsLoading(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    sendMessage(suggestion);
  };

  return (
    <div className="flex h-full overflow-hidden bg-background">
      <ConversationHistory
        conversations={conversations}
        currentConversation={currentConversation}
        onNewConversation={createNewConversation}
        onSelectConversation={selectConversation}
        mobileOpen={historyOpen}
        onMobileClose={() => setHistoryOpen(false)}
      />

      <div className="flex min-w-0 flex-1 flex-col">
        <ChatArea
          conversation={currentConversation || {
            id: "temp",
            title: "Nova Conversa",
            lastMessage: "",
            timestamp: new Date(),
            type: "consulta",
            messages: []
          }}
          isLoading={isLoading}
          onSendMessage={sendMessage}
          showSuggestions={!currentConversation || currentConversation.messages.length === 0}
          onSuggestionClick={handleSuggestionClick}
          obras={obras}
          obraId={obraId}
          onObraChange={setObraId}
          onOpenHistory={() => setHistoryOpen(true)}
        />
      </div>
    </div>
  );
};

export default AIAssistant;
